import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api, errMsg } from "../lib/api";
import { useQueue } from "../stores/queue";
import { usePrefs } from "../stores/prefs";
import { beep, vibrate } from "../lib/sound";

import { TopBar } from "../components/ui/TopBar";
import { Scanner } from "../components/ui/Scanner";
import { QtyStepper } from "../components/ui/QtyStepper";
import { Field } from "../components/ui/Field";
import { Badge } from "../components/ui/Badge";
import { Icon } from "../components/ui/Icon";
import { Button } from "../components/ui/Button";
import { Banner } from "../components/ui/Banner";
import { ProductSearch } from "../components/ui/ProductSearch";

type ProductLookup = {
  sku: string;
  name: string;
  image_url: string | null;
  quantity: number;
  low_stock_threshold: number;
};

const REASONS = [
  { value: "fornitore", label: "Fornitore" },
  { value: "reso_cliente", label: "Reso cliente" },
  { value: "correzione_inventario", label: "Correzione inventario" },
  { value: "trasferimento_altra_sede", label: "Trasferimento da altra sede" },
  { value: "altro", label: "Altro" },
];

export function StockIn() {
  const nav = useNavigate();
  const [sku, setSku] = useState("");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState(REASONS[0].value);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [eventDisplayId, setEventDisplayId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const prefs = usePrefs((s) => s.prefs);
  const enqueue = useQueue((s) => s.enqueue);

  const { data: product } = useQuery<ProductLookup>({
    queryKey: ["product-lookup", sku],
    queryFn: async () => (await api.get(`/api/stock/${sku}`)).data,
    enabled: !!sku && sku.length >= 3,
    retry: false,
    staleTime: 10_000,
  });

  function feedback(success: boolean) {
    if (prefs.beep) beep(success ? "ok" : "warn");
    if (prefs.vibrate) vibrate(success ? 30 : [40, 30, 40]);
  }

  function onScan(code: string) {
    setSku(code.toUpperCase().trim());
    feedback(true);
  }

  async function submit() {
    if (!sku || qty <= 0 || busy) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    const body = {
      event_type: "stock_in",
      sku,
      quantity_delta: qty,
      reason,
      notes: notes || null,
    };
    try {
      if (!navigator.onLine) {
        enqueue({ url: "/api/stock-event", method: "POST", body });
        setMsg(`Offline — accodato (+${qty} ${sku})`);
        setEventDisplayId(null);
        resetForm();
        return;
      }
      const { data } = await api.post("/api/stock-event", body, {
        headers: { "X-Idempotency-Key": crypto.randomUUID() },
      });
      feedback(true);
      setMsg(`+${qty} ${sku} confermato`);
      setEventDisplayId(formatEventId("EV-IN", data.id));
      resetForm();
    } catch (e) {
      feedback(false);
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  function resetForm() {
    setSku("");
    setNotes("");
    setQty(1);
  }

  const productCategory = inferCategory(sku);

  return (
    <>
      <TopBar back onBack={() => nav(-1)} title="Stock In" sub="Arrivo merce" />

      <div style={{ padding: "14px 18px 8px", display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            background: "var(--tcg-success-bg)",
            color: "var(--tcg-success)",
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.06em",
          }}
        >
          ENTRATA · IN
        </div>
        <span
          className="tcg-mono"
          style={{ fontSize: 10, color: "var(--tcg-muted)", fontWeight: 700, marginLeft: "auto" }}
        >
          {eventDisplayId || nextEventPlaceholder("EV-IN")}
        </span>
      </div>

      <div style={{ padding: "0 18px" }}>
        <Scanner onDetect={onScan} paused={busy} hint="Inquadra il QR / barcode del prodotto" />
      </div>

      <div style={{ padding: "12px 18px 0" }}>
        <ProductSearch
          selectedSku={sku}
          onPick={(p) => {
            setSku(p.sku);
            feedback(true);
          }}
        />
      </div>

      {product && (
        <div style={{ padding: "12px 18px 0" }}>
          <div
            className="tcg-card"
            style={{
              padding: 12,
              display: "flex",
              gap: 12,
              alignItems: "center",
              borderColor: "var(--tcg-success)",
              background: "linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 60%)",
            }}
          >
            <ScanThumb imageUrl={product.image_url} cat={productCategory} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.25 }}>{product.name}</div>
              <div
                className="tcg-mono"
                style={{ fontSize: 10, color: "var(--tcg-muted)", marginTop: 2, fontWeight: 700 }}
              >
                {product.sku}
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                <Badge tone="success">
                  <Icon name="check" size={10} /> Riconosciuto
                </Badge>
                <Badge tone="navy">{productCategory.label}</Badge>
                <Badge tone="ghost">Stock: {product.quantity}</Badge>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: "14px 18px 0" }}>
        <Field label="SKU (manuale o scansionato)">
          <input
            className="tcg-input tcg-mono"
            value={sku}
            onChange={(e) => setSku(e.target.value.toUpperCase().trim())}
            placeholder="POK-TER-151-IT-ETB"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        <Field label="Quantità ricevuta" hint="Default 1 — usa +/- o digita la quantità">
          <QtyStepper value={qty} onChange={setQty} min={1} />
        </Field>

        <Field label="Ragione entrata">
          <select className="tcg-select" value={reason} onChange={(e) => setReason(e.target.value)}>
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Note (opzionale)">
          <textarea
            className="tcg-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Es. fornitore, numero fattura, riferimenti…"
          />
        </Field>

        {err && (
          <Banner tone="danger" icon="x">
            {err}
          </Banner>
        )}
        {msg && !err && (
          <Banner tone="success" icon="check">
            {msg}
          </Banner>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <Button variant="secondary" onClick={() => nav(-1)} disabled={busy}>
            Annulla
          </Button>
          <Button variant="yellow" style={{ flex: 1 }} disabled={!sku || busy} onClick={submit}>
            <Icon name="check" size={16} />
            {busy ? "Conferma…" : `Conferma entrata · +${qty}`}
          </Button>
        </div>
      </div>

      <div style={{ height: 24 }} />
    </>
  );
}

function ScanThumb({ imageUrl, cat }: { imageUrl: string | null; cat: CategoryMeta }) {
  const [errored, setErrored] = useState(false);
  const showImg = imageUrl && !errored;
  return (
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 11,
        background: showImg
          ? "var(--tcg-bg)"
          : cat.thumbBg || "repeating-linear-gradient(135deg, #FFE4D6 0 8px, #FFD4BA 8px 16px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        overflow: "hidden",
        color: cat.thumbFg,
        fontSize: 9,
        fontWeight: 800,
        textAlign: "center",
        lineHeight: 1.1,
        whiteSpace: "pre",
      }}
    >
      {showImg ? (
        <img
          src={imageUrl!}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        cat.thumbLabel
      )}
    </div>
  );
}

function formatEventId(prefix: string, id: number): string {
  return `${prefix}-${String(id).padStart(4, "0")}`;
}

function nextEventPlaceholder(prefix: string): string {
  return `${prefix}-····`;
}

type CategoryMeta = {
  label: string;
  thumbBg: string | null;
  thumbFg: string;
  thumbLabel: string;
};

function inferCategory(sku: string): CategoryMeta {
  const first = sku.split("-")[0]?.toUpperCase() || "";
  switch (first) {
    case "POK":
      return {
        label: "Pokémon",
        thumbBg: "repeating-linear-gradient(135deg, #FFE4D6 0 8px, #FFD4BA 8px 16px)",
        thumbFg: "#9A3412",
        thumbLabel: "POK",
      };
    case "OP":
      return {
        label: "One Piece",
        thumbBg: "repeating-linear-gradient(135deg, #DBEAFE 0 8px, #BFDBFE 8px 16px)",
        thumbFg: "#1E40AF",
        thumbLabel: "OP",
      };
    case "YGO":
      return {
        label: "Yu-Gi-Oh!",
        thumbBg: "repeating-linear-gradient(135deg, #FCE7F3 0 8px, #FBCFE8 8px 16px)",
        thumbFg: "#9D174D",
        thumbLabel: "YGO",
      };
    case "LOR":
      return {
        label: "Lorcana",
        thumbBg: "repeating-linear-gradient(135deg, #FFEDD5 0 8px, #FED7AA 8px 16px)",
        thumbFg: "#C2410C",
        thumbLabel: "LOR",
      };
    case "FUN":
      return {
        label: "Funko",
        thumbBg: "repeating-linear-gradient(135deg, #CCFBF1 0 8px, #99F6E4 8px 16px)",
        thumbFg: "#0F766E",
        thumbLabel: "FUN",
      };
    case "DB":
      return {
        label: "Dragon Ball",
        thumbBg: "repeating-linear-gradient(135deg, #FEF3C7 0 8px, #FDE68A 8px 16px)",
        thumbFg: "#92400E",
        thumbLabel: "DB",
      };
    case "MTG":
      return {
        label: "Magic",
        thumbBg: "repeating-linear-gradient(135deg, #E6EBF8 0 8px, #C7D2FE 8px 16px)",
        thumbFg: "#1B3A8C",
        thumbLabel: "MTG",
      };
    default:
      return {
        label: "Altro",
        thumbBg: null,
        thumbFg: "#3E4255",
        thumbLabel: first.slice(0, 3) || "???",
      };
  }
}
